/**
 * Event Processed Event Component
 *
 * Renders ahoy_event_processed events (automation run summaries) on the
 * timeline. Body: payload.summary as org-scoped markdown (entity tags +
 * GitHub alert blocks resolve). status='error' gets a distinct treatment.
 */
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { cx } from '@/utils/cx';

import { useValidOrgId } from '../../hooks/useValidOrgId';
import {
  ActorName,
  InlineText,
  MARKDOWN_ALERT_CLASSNAME,
  TimelineEvent,
} from '../components/shared';
import type { EventProcessedPayload } from '../types';
import { useEventFormatters } from '../useEventFormatters';
import { parseNonEmptyString } from '../utils/payloadValidation';
import type { EventComponentProps } from './types';

export function EventProcessedEvent({ event }: EventComponentProps) {
  const { getActorInfo } = useEventFormatters();
  const actor = getActorInfo(event);

  const validOrgId = useValidOrgId();

  const payload = event.payload as EventProcessedPayload | undefined;
  const summary = parseNonEmptyString(payload?.summary);
  const isError = payload?.status === 'error';

  const ariaLabel = isError
    ? `${actor.name} processed an event – error`
    : `${actor.name} processed an event`;

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <InlineText>
            <ActorName actor={actor} /> processed an event
          </InlineText>
        }
        timestamp={event.occurred_at}
      >
        {summary && validOrgId !== undefined && (
          <div
            data-status={isError ? 'error' : 'success'}
            className={cx(
              'mt-2 rounded-md border px-3 py-2',
              isError
                ? 'border-[var(--color-border-error)] bg-[var(--color-bg-error-secondary)]'
                : 'border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)]'
            )}
          >
            <MarkdownContent
              content={summary}
              orgId={validOrgId}
              size='compact'
              className={MARKDOWN_ALERT_CLASSNAME}
            />
          </div>
        )}
      </TimelineEvent.Content>
    </TimelineEvent>
  );
}
